import * as React from "react";
import { cn } from "../../lib/utils";
import {
  ChevronDown,
  ChevronUp,
  Download,
  FileJson,
  FileSpreadsheet,
  AlertTriangle,
  RefreshCw,
  type LucideIcon,
} from "lucide-react";
import { Button } from "../button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../dropdown-menu";
import { Skeleton } from "../skeleton";
import { ModuleScore } from "./ModuleScore";

export interface ModuleContainerProps {
  /** Module identifier (e.g., 'dns', 'email', 'tls') */
  moduleName: string;
  /** Icon component for the module */
  icon: LucideIcon;
  /** Module data to display */
  data: unknown;
  /** Loading state */
  isLoading?: boolean;
  /** Error message if module failed */
  error?: string | null;
  /** Optional score (0-100) */
  score?: number;
  /** Optional grade letter (A-F) */
  grade?: string;
  /** Child components to render */
  children: React.ReactNode;
  /** Callback when retry is clicked */
  onRetry?: () => void;
  /** Custom class name */
  className?: string;
  /** Whether to start expanded */
  defaultExpanded?: boolean;
  /** Hide the header (useful when used within tabs that already identify the module) */
  hideHeader?: boolean;
}

// Format module name for display
function formatModuleName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/-/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// Export data as JSON
function exportAsJson(data: unknown, moduleName: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${moduleName}_${new Date().toISOString().split("T")[0]}.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

// Export data as CSV (for array-like data)
function exportAsCsv(data: unknown, moduleName: string) {
  let csvContent = "";

  if (Array.isArray(data)) {
    if (data.length > 0 && typeof data[0] === "object") {
      const headers = Object.keys(data[0] as object);
      csvContent = headers.join(",") + "\n";
      csvContent += data
        .map((row) =>
          headers
            .map((header) => {
              const value = (row as Record<string, unknown>)[header];
              const stringValue = value === null || value === undefined ? "" : String(value);
              return `"${stringValue.replace(/"/g, '""')}"`;
            })
            .join(",")
        )
        .join("\n");
    }
  } else if (typeof data === "object" && data !== null) {
    const entries = Object.entries(data);
    csvContent = "Key,Value\n";
    csvContent += entries
      .map(([key, value]) => `"${key}","${String(value).replace(/"/g, '""')}"`)
      .join("\n");
  }

  const blob = new Blob([csvContent], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `${moduleName}_${new Date().toISOString().split("T")[0]}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

export function ModuleContainer({
  moduleName,
  icon: Icon,
  data,
  isLoading = false,
  error,
  score,
  grade,
  children,
  onRetry,
  className,
  defaultExpanded = true,
  hideHeader = false,
}: ModuleContainerProps) {
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);
  const [allSectionsExpanded, setAllSectionsExpanded] = React.useState(true);

  // Provide context for child components to listen to expand/collapse all
  const contextValue = React.useMemo(
    () => ({ allSectionsExpanded }),
    [allSectionsExpanded]
  );

  if (isLoading) {
    return (
      <div className={cn("rounded-lg border border-border bg-card/50 overflow-hidden", className)}>
        <div className="flex items-center gap-4 p-4 border-b border-border bg-muted/30">
          <Skeleton className="h-10 w-10 rounded-lg" />
          <div className="flex-1">
            <Skeleton className="h-5 w-32 mb-2" />
            <Skeleton className="h-4 w-24" />
          </div>
        </div>
        <div className="p-6 space-y-4">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn("rounded-lg border border-red-500/30 bg-red-500/5 overflow-hidden", className)}>
        <div className="flex items-center gap-4 p-4 border-b border-red-500/20 bg-red-500/10">
          <div className="p-2.5 rounded-lg bg-red-500/20">
            <Icon className="h-5 w-5 text-red-400" />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-red-50">{formatModuleName(moduleName)}</h3>
            <p className="text-sm text-red-400/80">Module failed to execute</p>
          </div>
        </div>
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4">
            <AlertTriangle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-muted-foreground">{error}</p>
          </div>
          {onRetry && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRetry}
              className="gap-2 border-red-500/30 hover:bg-red-500/10"
            >
              <RefreshCw className="h-4 w-4" />
              Retry Module
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <ModuleContainerContext.Provider value={contextValue}>
      <div className={cn(
        "overflow-hidden",
        !hideHeader && "rounded-lg border border-border bg-card/50",
        className
      )}>
        {/* Header - hidden when used within tabs */}
        {!hideHeader && (
          <div className="flex items-center gap-4 p-4 border-b border-border bg-muted/30">
            <div className="p-2.5 rounded-lg bg-cyan-500/10 border border-cyan-500/20">
              <Icon className="h-5 w-5 text-cyan-400" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-foreground truncate">
                  {formatModuleName(moduleName)}
                </h3>
                {(score !== undefined || grade) && (
                  <ModuleScore score={score} grade={grade} size="sm" />
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {/* Expand/Collapse All */}
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setAllSectionsExpanded(!allSectionsExpanded)}
                className="gap-1.5 text-muted-foreground hover:text-foreground"
              >
                {allSectionsExpanded ? (
                  <>
                    <ChevronUp className="h-4 w-4" />
                    <span className="text-xs hidden sm:inline">Collapse</span>
                  </>
                ) : (
                  <>
                    <ChevronDown className="h-4 w-4" />
                    <span className="text-xs hidden sm:inline">Expand</span>
                  </>
                )}
              </Button>

              {/* Export Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-1.5">
                    <Download className="h-4 w-4" />
                    <span className="text-xs hidden sm:inline">Export</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => exportAsJson(data, moduleName)}>
                    <FileJson className="h-4 w-4 mr-2" />
                    Export as JSON
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => exportAsCsv(data, moduleName)}>
                    <FileSpreadsheet className="h-4 w-4 mr-2" />
                    Export as CSV
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Toggle Content */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(!isExpanded)}
                className="h-8 w-8"
              >
                {isExpanded ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Content */}
        {isExpanded && <div className={hideHeader ? "" : "p-6"}>{children}</div>}
      </div>
    </ModuleContainerContext.Provider>
  );
}

// Context for expand/collapse all functionality
interface ModuleContainerContextValue {
  allSectionsExpanded: boolean;
}

const ModuleContainerContext = React.createContext<ModuleContainerContextValue>({
  allSectionsExpanded: true,
});

export function useModuleContainerContext() {
  return React.useContext(ModuleContainerContext);
}

// Collapsible section component for use within ModuleContainer
export interface ModuleSectionProps {
  title: string;
  icon?: LucideIcon;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  className?: string;
}

export function ModuleSection({
  title,
  icon: Icon,
  badge,
  children,
  defaultExpanded = true,
  className,
}: ModuleSectionProps) {
  const { allSectionsExpanded } = useModuleContainerContext();
  const [isExpanded, setIsExpanded] = React.useState(defaultExpanded);

  // Sync with parent expand/collapse all
  React.useEffect(() => {
    setIsExpanded(allSectionsExpanded);
  }, [allSectionsExpanded]);

  return (
    <div className={cn("border border-border rounded-lg overflow-hidden", className)}>
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center gap-3 p-4 bg-muted/30 hover:bg-muted/50 transition-colors text-left"
      >
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" />}
        <span className="flex-1 font-medium text-sm">{title}</span>
        {badge}
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {isExpanded && <div className="p-4 border-t border-border">{children}</div>}
    </div>
  );
}

ModuleContainer.displayName = "ModuleContainer";
ModuleSection.displayName = "ModuleSection";
